import { Avatar, AvatarFallback, getInitials } from '@/components/health-ui';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isAdminPrincipal } from '@/components/app-nav-items';
import { ThemeToggle } from '@/components/theme-toggle';
import { useAuth } from '@/lib/auth-guards';
import { createTestIdHelper } from '@/lib/utils';
import { OMNIWOUND_ROLES } from '@omniwound/auth-contract';

const topBarTestId = createTestIdHelper('mobile-top-bar');

/**
 * Mobile header, shown below `lg` where the sidebar is hidden.
 *
 * Navigation lives in the bottom tab bar, so this carries branding plus the
 * identity/theme/sign-out controls the sidebar footer owns on desktop.
 */
export function MobileTopBar(): React.JSX.Element {
  const { t } = useTranslation('common');
  const { logout, user, realmRoles } = useAuth();

  const displayName =
    user?.fullName ??
    user?.email ??
    user?.preferredUsername ??
    user?.username ??
    t('shell.defaultUser');
  const roleLabel = isAdminPrincipal(realmRoles)
    ? t('shell.roleAdmin')
    : realmRoles.includes(OMNIWOUND_ROLES.PROVIDER)
      ? t('shell.roleProvider')
      : t('shell.roleSignedIn');

  return (
    <header
      className="sticky top-0 z-30 flex h-[var(--mobile-tab-bar-h)] items-center gap-3 border-b border-border bg-card px-4 lg:hidden"
      data-testid={topBarTestId()}
    >
      <img src="/logo-mark.svg" alt="" className="size-7 shrink-0 rounded-lg" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-display text-sm font-semibold">
          {displayName}
        </span>
        <span className="truncate text-[11px] text-muted-foreground">
          {roleLabel}
        </span>
      </div>
      <Avatar size="sm">
        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
      </Avatar>
      <ThemeToggle className="size-11" />
      <button
        type="button"
        aria-label={t('nav.signOut')}
        className="flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        data-testid={topBarTestId('sign-out')}
        onClick={() => void logout()}
      >
        <LogOut className="size-5" />
      </button>
    </header>
  );
}
